<template>
  <div class="tw-flex tw-flex-col tw-gap-3 tw-text-sm">
    <div v-if="isFetching && !sim" class="tw-flex tw-items-center tw-py-2 tw-text-gray-400">
      <span class="tw-w-4 tw-h-4 tw-border-2 tw-border-gray-300 tw-border-t-blue-400 tw-rounded-full tw-animate-spin tw-mr-2" />
      SIM-gegevens ophalen...
    </div>
    <div v-else-if="error" class="tw-text-red-600 tw-bg-red-50 tw-rounded tw-p-2">
      {{ error.message || 'SIM-gegevens ophalen mislukt' }}
    </div>

    <template v-else-if="sim">
      <!-- Errors from one of the sources: the rest did load -->
      <div v-for="(msg, i) in sim.errors" :key="i" class="tw-text-xs tw-text-amber-700 tw-bg-amber-50 tw-rounded tw-p-2">
        {{ msg }}
      </div>

      <!-- Live: our own 1NCE account, or the network status Cast4all reports for a SIM in its account -->
      <div v-if="liveRows.length" class="tw-flex tw-flex-col tw-gap-1">
        <div class="tw-flex tw-items-center tw-justify-between">
          <div class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
            Live <span class="tw-font-normal tw-normal-case tw-text-gray-400">{{ sim.once ? '1NCE' : 'Cast4all' }}</span>
          </div>
          <button
            class="tw-text-xs tw-text-blue-500 hover:tw-text-blue-700 disabled:tw-text-gray-300"
            :disabled="isFetching"
            @click="refetch()"
          >Vernieuwen</button>
        </div>
        <div class="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1">
          <template v-for="row in liveRows">
            <span :key="`${row.label}-label`" class="tw-text-gray-500">{{ row.label }}</span>
            <span
              :key="`${row.label}-value`"
              class="tw-font-medium tw-break-all"
              :class="[row.class, { 'tw-font-mono tw-text-xs tw-self-center': row.mono, 'tw-cursor-pointer hover:tw-text-blue-600': row.copy }]"
              :title="row.copy ? 'Klik om te kopiëren' : ''"
              @click="row.copy && copyToClipboard(row.value)"
            >{{ row.value }}</span>
          </template>
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <div class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
          Database <span class="tw-font-normal tw-normal-case tw-text-gray-400">{{ SOURCE_LABELS[sim.source] || sim.source }}</span>
        </div>
        <div class="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1">
          <template v-for="row in dbRows">
            <span :key="`${row.label}-label`" class="tw-text-gray-500">{{ row.label }}</span>
            <span
              :key="`${row.label}-value`"
              class="tw-font-medium tw-break-all"
              :class="[row.class, { 'tw-font-mono tw-text-xs tw-self-center': row.mono, 'tw-cursor-pointer hover:tw-text-blue-600': row.copy }]"
              :title="row.copy ? 'Klik om te kopiëren' : ''"
              @click="row.copy && copyToClipboard(row.value)"
            >{{ row.value }}</span>
          </template>
        </div>
        <div v-if="!liveRows.length" class="tw-text-xs tw-text-gray-400">
          {{ sim.provider === '1nce' ? 'Geen live 1NCE-gegevens.' : `${sim.provider_name || 'Deze provider'} heeft geen API: alleen de gegevens uit de database.` }}
        </div>
      </div>

      <!-- Reset -->
      <div class="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-pt-2 tw-border-t tw-border-gray-100">
        <span class="tw-text-xs tw-text-gray-500">{{ resetHint }}</span>
        <button
          class="tw-bg-amber-500 hover:tw-bg-amber-600 tw-text-white tw-rounded-lg tw-py-1 tw-px-2.5 tw-font-semibold tw-text-xs tw-transition-colors tw-flex-shrink-0 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
          :disabled="!sim.can_reset || isResetting"
          :title="sim.can_reset ? '' : resetDisabledReason"
          @click="handleReset"
        >
          <span class="tw-flex tw-items-center tw-gap-1">
            <span v-if="isResetting" class="tw-w-3 tw-h-3 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-animate-spin" />
            SIM resetten
          </span>
        </button>
      </div>
    </template>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useQuery, useMutation } from '@tanstack/vue-query'
import fetchData from '@/util/fetch'
import { notifications } from '@/util'
import { copyToClipboard } from '@/util/Clipboard'
import { formatDateNL, formatDateTimeNL } from './formatters'

// The SIM of a meter, in the SIM modal: live from 1NCE (our own account) or Cast4all where there is a live
// source, and always the whole database record (polling.simcard, else energize; never the password).
// Wireless Logic has no API, so for its SIMs the database is all there is.
const props = defineProps({
  serialNumber: { type: String, required: true },
})

const SOURCE_LABELS = { polling: 'polling server', energize: 'energize', c4a: 'Cast4all' }

// Live from 1NCE (and Cast4all): fetched when the modal opens, and not cached

const { data: sim, isFetching, error, refetch } = useQuery({
  queryKey: computed(() => ['meterSim', props.serialNumber]),
  queryFn: async () => {
    const result = await fetchData(`/eleena/api/v1/meters/${props.serialNumber}/sim`)
    return result?.data ?? null
  },
  enabled: computed(() => !!props.serialNumber),
  refetchOnWindowFocus: false,
  staleTime: 0,
  cacheTime: 0,
  retry: false,
})

const CONNECTIVITY_CLASSES = { ONLINE: 'tw-text-green-600', ATTACHED: 'tw-text-amber-600', OFFLINE: 'tw-text-red-600' }

const formatMb = (mb) => (mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`)

const present = (rows) => rows.filter((row) => row.value != null && row.value !== '')

const liveRows = computed(() => {
  const s = sim.value
  if (!s) return []
  const once = s.once ?? {}
  const simStatus = { Enabled: 'Actief', Disabled: 'Uitgeschakeld' }[once.sim_status] ?? once.sim_status
  const network = [once.network_operator, once.network_country].filter(Boolean).join(', ')
  return present([
    { label: 'Verbinding', value: once.connectivity, class: CONNECTIVITY_CLASSES[once.connectivity] },
    { label: 'SIM', value: simStatus, class: once.sim_status === 'Enabled' ? 'tw-text-green-600' : 'tw-text-red-600' },
    { label: 'Netwerk', value: network ? `${network}${once.rat_type ? ` (${once.rat_type})` : ''}` : null },
    { label: 'Netwerkstatus (Cast4all)', value: s.c4a?.network_status },
    { label: 'Locatie bijgewerkt', value: once.location_updated && formatDateTimeNL(once.location_updated) },
    {
      label: 'Datategoed',
      value: once.data_volume_mb != null
        ? `${formatMb(once.data_volume_mb)}${once.data_total_volume_mb ? ` van ${formatMb(once.data_total_volume_mb)}` : ''}`
        : null,
    },
    { label: 'Geldig tot', value: once.data_expiry_date && formatDateNL(once.data_expiry_date) },
    { label: 'Tegoedstatus', value: once.quota_status },
    { label: 'IMEI', value: once.imei, mono: true },
    { label: 'Geactiveerd', value: once.activation_date && formatDateNL(once.activation_date) },
  ])
})

// the whole record, empty fields as a dash so it is clear they are empty
const dbRows = computed(() => {
  const s = sim.value
  if (!s) return []
  return [
    { label: 'Provider', value: s.provider_name },
    { label: 'Operator', value: s.operator },
    { label: 'ICCID', value: s.icc_id, mono: true, copy: true },
    { label: 'MSISDN', value: s.msisdn, mono: true },
    { label: 'IP-adres', value: s.ip_address, mono: true },
    { label: 'Poort', value: s.port_number, mono: true },
    { label: 'Server-/client-W-poort', value: s.server_w_port != null || s.client_w_port != null ? `${s.server_w_port ?? '—'} / ${s.client_w_port ?? '—'}` : null, mono: true },
    { label: 'Referencing', value: s.referencing_method },
    { label: 'VPN', value: s.vpn_type },
    { label: 'Laatste reset', value: s.last_reset_at && formatDateTimeNL(s.last_reset_at) },
  ].map((row) => (row.value == null || row.value === '' ? { ...row, value: '—', mono: false, copy: false } : row))
})

const resetDisabledReason = computed(() => {
  if (!sim.value) return ''
  if (sim.value.provider !== '1nce') return 'Alleen een 1NCE-SIM kan gereset worden'
  if (sim.value.is_c4a) return 'Deze SIM valt onder het 1NCE-account van Cast4all: live status en reset lopen via Cast4all'
  if (!sim.value.icc_id) return 'Geen ICCID bekend'
  return '1NCE is niet geconfigureerd op de server'
})

const resetHint = computed(() =>
  sim.value?.can_reset
    ? 'Reset verbreekt de netwerkregistratie, de meter verbindt daarna opnieuw.'
    : resetDisabledReason.value
)

const { mutate: resetSim, isLoading: isResetting } = useMutation({
  mutationFn: () => fetchData(`/eleena/api/v1/meters/${props.serialNumber}/sim/reset`, { method: 'POST' }),
  onSuccess: () => {
    notifications.addNotification({ message: 'SIM-reset aangevraagd bij 1NCE', type: 'success' })
    // 1NCE needs a moment to re-establish the connection
    setTimeout(() => refetch(), 5000)
  },
  onError: (e) => {
    notifications.addNotification({ message: e?.message || 'SIM-reset mislukt', type: 'danger' })
  },
})

const handleReset = () => {
  if (!window.confirm(`SIM van meter ${props.serialNumber} resetten bij 1NCE?`)) return
  resetSim()
}
</script>
